import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_3 = {
  key: 0,
  class: "mt-4 flex bg-gray-50 px-4 py-4 shadow ring-1 ring-gray-300 sm:p-6 md:rounded"
}
const _hoisted_4 = { class: "text-center text-xs font-medium text-gray-500" }
const _hoisted_5 = { class: "mt-1 text-center text-2xl font-semibold text-gray-800" }
const _hoisted_6 = {
  key: 2,
  class: "mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2"
}

import { ref, Ref, computed, watch, ComputedRef, onMounted } from 'vue';
import moment from 'moment';
import store from '@/store';
import { useI18n } from 'vue-i18n';
import { useFilterStore, useUiStore } from '@/stores';
import { toastServiceError } from '@/services/core/notification';
import { AgentProductivityPerDayReportService } from '@/services';

import {
    AgentProductivityPerDayReportActions,
    AgentProductivityPerDayReportGetters,
    BarChartConfig,
    CallType,
    ChartDataItem,
    DataItem,
    AgentProductivityPerDayReportFilter,
    AgentProductivityPerDayReportServiceGetInfoParams,
    AgentProductivityPerDayReportServiceGetInfoResult,
    CallsStatsPerDay,
    FilterItem,
    LevelModel,
} from '@/types';

import { AgentProductivityPerDayReportFilters } from '@/views/pages/reports/agent-productivity-per-day/components';
import WorkScheduleService from '@/services/api/WorkScheduleService';


export default /*@__PURE__*/_defineComponent({
  __name: 'AgentProductivityPerDayReportPage',
  setup(__props) {

const reportInfo: Ref<AgentProductivityPerDayReportServiceGetInfoResult | null> = ref(null);

const levelFilter = computed<LevelModel | null>(() => filterStore.level);
const dateRange = computed<Array<Date>>({
    get: () => filterStore.dateRange,
    set: (dateRange: Array<Date>) => filterStore.setDateRange(dateRange),
});
const filter = computed<AgentProductivityPerDayReportFilter>(
    () => store.getters[AgentProductivityPerDayReportGetters.FILTER],
);
const activeFilters: ComputedRef<FilterItem[]> = computed(
    () => store.getters[AgentProductivityPerDayReportGetters.ACTIVE_FILTERS],
);

const filterStore = useFilterStore();
const uiStore = useUiStore();
const { t } = useI18n();
const hasActiveWorkSchedule = ref(false);

watch([levelFilter, dateRange, activeFilters], () => {
    fetchReportData();
});

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchReportData = async () => {
    const params: AgentProductivityPerDayReportServiceGetInfoParams = {
        startDate: moment.utc(dateRange.value[0]).format('YYYY-MM-DD'),
        endDate: moment.utc(dateRange.value[1]).format('YYYY-MM-DD'),
        user: filter.value.user?.id || null,
        userLabel: filter.value.userLabel?.id || null,
    };

    try {
        setLoading(true);
        const response = await AgentProductivityPerDayReportService.getInfo({ params });
        reportInfo.value = response.data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const handleRemoveFilter = (filter: FilterItem) => {
    store.dispatch(AgentProductivityPerDayReportActions.CLEAR_FILTER, filter.field);
};

const headerStats = computed<DataItem<string>[]>(() => {
    const stats = reportInfo.value?.stats;
    if (!stats) return [];

    return [
        { name: 'totalCalls', value: stats.total.toString() },
        { name: 'inboundCalls', value: stats.type[CallType.INBOUND].toString() },
        { name: 'outboundCalls', value: stats.type[CallType.OUTBOUND].toString() },
        { name: 'internalCalls', value: stats.type[CallType.INTERNAL].toString() },
        { name: 'uniqueNumbers', value: stats.uniqueNumbers.toString() },
        {
            name: 'averageServiceTime',
            value: moment.utc((stats.averageServiceTime || 0) * 1000).format('HH:mm:ss'),
        },
    ];
});

const yAxisLabelFormatterAsTime = (value: number | string) => {
    value = value || 0;
    return moment.utc((value as number) * 1000).format('HH:mm:ss');
};

const xAxisLabelFormatterAsDate = (value: number | string) => {
    return moment.utc(value).format('DD/MM');
};

const getChart = (
    title: string,
    prop: keyof CallsStatsPerDay,
    color = '#BFDBFE',
    yAxisLabelFormatter = (value: number | string) => String(value),
): BarChartConfig => {
    const stats = reportInfo.value?.callsStatsPerDay || [];
    const category: string[] = [];
    const serie: ChartDataItem<number[]> = {
        name: title,
        value: [],
        color,
    };

    for (let i = stats.length - 1; i >= 0; i--) {
        const item = stats[i];
        category.push(item.date);
        serie.value.push(item[prop] as number);
    }

    return {
        title,
        category: category.reverse(),
        series: [{ ...serie, value: serie.value.reverse() }],
        yAxisLabelFormatter,
    };
};

const charts = computed<BarChartConfig[]>(() => [
    getChart(
        t('report.agentProductivityPerDay.serviceTimePerDayColumnChart.title'),
        'averageServiceTime',
        '#BFDBFE',
        yAxisLabelFormatterAsTime,
    ),
    getChart(
        t('report.agentProductivityPerDay.timeAvailablePerDayColumnChart.title'),
        'averageTimeAvailable',
        '#FBCFE8',
        yAxisLabelFormatterAsTime,
    ),
    getChart(
        t('report.agentProductivityPerDay.durationPerDayColumnChart.title'),
        'averageDuration',
        '#A5F3FC',
        yAxisLabelFormatterAsTime,
    ),
    getChart(
        t('report.agentProductivityPerDay.totalIdleTimePerDayColumnChart.title'),
        'averageTotalIdleTime',
        '#BAE6FD',
        yAxisLabelFormatterAsTime,
    ),
    getChart(
        t('report.agentProductivityPerDay.idleTimePerDayColumnChart.title'),
        'averageIdleTime',
        '#F5D0FE',
        yAxisLabelFormatterAsTime,
    ),
]);

const visibleCharts = computed(() => {
    if (hasActiveWorkSchedule.value) return charts.value;
    return charts.value.filter((_, index) => index !== 3);
});

onMounted(async () => {
    fetchReportData();

    const res = await WorkScheduleService.getAll();
    hasActiveWorkSchedule.value = res.data.some((workSchedule) => workSchedule.isActive);
});

return (_ctx: any,_cache: any) => {
  const _component_UiDatePicker = _resolveComponent("UiDatePicker")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiBadge = _resolveComponent("UiBadge")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_CallTypePerDayColumnChart = _resolveComponent("CallTypePerDayColumnChart")!
  const _component_CallStatusPerDayColumnChart = _resolveComponent("CallStatusPerDayColumnChart")!
  const _component_BarChart = _resolveComponent("BarChart")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t('report.agentProductivityPerDay.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createVNode(_unref(AgentProductivityPerDayReportFilters)),
        _createVNode(_component_UiDatePicker, {
          modelValue: dateRange.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dateRange).value = $event)),
          range: "",
          multiCalendars: "",
          class: "ml-4"
        }, null, 8, ["modelValue"])
      ]),
      _: 1
    }),
    (activeFilters.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeFilters.value, (filter) => {
            return (_openBlock(), _createBlock(_component_UiBadge, {
              key: filter,
              removable: "",
              onRemove: ($event: any) => (handleRemoveFilter(filter))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t(`report.agentProductivityPerDay.filters.activeTag.${filter.field}`)) + ": " + _toDisplayString(filter.label), 1)
              ]),
              _: 2
            }, 1032, ["onRemove"]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (headerStats.value)
      ? (_openBlock(), _createElementBlock("dl", {
          key: 1,
          class: _normalizeClass(["mt-4 grid grid-cols-2 gap-4", ['sm:grid-cols-' + headerStats.value.length]])
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerStats.value, (stat) => {
            return (_openBlock(), _createBlock(_component_UiPanel, {
              key: stat.name
            }, {
              default: _withCtx(() => [
                _createElementVNode("dt", _hoisted_4, _toDisplayString(_ctx.$t('report.contactCenterPerAgent.stats.' + stat.name)), 1),
                _createElementVNode("dd", _hoisted_5, _toDisplayString(stat.value), 1)
              ]),
              _: 2
            }, 1024))
          }), 128))
        ], 2))
      : _createCommentVNode("", true),
    (reportInfo.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createVNode(_component_CallTypePerDayColumnChart, {
            title: _unref(t)('report.agentProductivityPerDay.callTypePerDayColumnChart.title'),
            data: reportInfo.value.callsStatsPerDay,
            class: "col-span-2 h-[300px] md:col-span-1"
          }, null, 8, ["title", "data"]),
          _createVNode(_component_CallStatusPerDayColumnChart, {
            title: _unref(t)('report.agentProductivityPerDay.callStatusPerDayColumnChart.title'),
            data: reportInfo.value.callsStatsPerDay,
            class: "col-span-2 h-[300px] md:col-span-1"
          }, null, 8, ["title", "data"]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleCharts.value, (chart, index) => {
            return (_openBlock(), _createBlock(_component_BarChart, {
              key: index,
              title: chart.title,
              categoryData: chart.category,
              seriesData: chart.series,
              xAxisLabelFormatter: xAxisLabelFormatterAsDate,
              yAxisLabelFormatter: chart.yAxisLabelFormatter,
              column: "",
              class: "col-span-2 h-[300px] md:col-span-1"
            }, null, 8, ["title", "categoryData", "seriesData", "yAxisLabelFormatter"]))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})